import { useEffect } from "react"
import { X } from "lucide-react"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../ui/select"
import { Button } from "../ui/button"
import Spinner from "../ui/spinner"
import { useTranslation } from "@/hooks/useTranslation"

interface Country {
    id: number
    value: string
}

interface CurrentCountryData {
    name: string
    id: string
}

interface CountrySelectionModalProps {
    isOpen: boolean
    onClose: () => void
    countries: Country[]
    currentCountryData: CurrentCountryData | null
    onCountrySelect: (countryId: string) => Promise<void>
}

const CountrySelectionModal = ({
    isOpen,
    onClose,
    countries,
    currentCountryData,
    onCountrySelect
}: CountrySelectionModalProps) => {
    const { t } = useTranslation()

    // Bloquear scroll del body cuando el modal esté abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup al desmontar
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Cerrar con tecla Escape
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    const handleCountrySelect = async (value: string) => {
        await onCountrySelect(value)
        onClose()
    }

    if (!isOpen) {
        return null
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center">
            {/* Overlay oscuro */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={handleOverlayClick}
                aria-hidden="true"
            />

            {/* Modal centrado */}
            <div className="relative bg-white dark:bg-card rounded-lg shadow-xl p-6 w-full max-w-md mx-4 z-10">
                {/* Header con título y botón cerrar */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Seleccione el país
                    </h2>
                    <button
                        type="button"
                        className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                        onClick={onClose}
                    >
                        <span className="sr-only">Cerrar</span>
                        <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>

                {/* Dropdown de países */}
                <div className="w-full space-y-4">
                    <Select
                        value={currentCountryData?.id || ""}
                        onValueChange={handleCountrySelect}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecciona un país">
                                {currentCountryData?.name || "Selecciona un país"}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] !z-[100000]">
                            {
                                countries.map((country) => (
                                    <SelectItem
                                        key={country.id}
                                        value={country.id.toString()}
                                    >
                                        {country.value}
                                    </SelectItem>
                                ))
                            }
                        </SelectContent>
                    </Select>

                    {/* Botón Aceptar */}
                    <div className="flex justify-center mt-2">
                        <Button
                            onClick={onClose}
                            className="px-6"
                        >
                            {t('buttons.confirm')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CountrySelectionModal
